/** Paid-wall families aligned with product permission rows (discovery/workflow/insight/reporting). */
export type PaidWallFamily = 'discovery' | 'workflow' | 'insight' | 'reporting';

/** Stable feature identifiers used in route data, query params, and upsell copy. */
export type PaidWallFeatureId =
  | 'search'
  | 'projects'
  | 'map-keyword'
  | 'map-advanced-filters'
  | 'dashboard-breakdown'
  | 'reporting'
  | 'add-to-project';

/** Route `data.paidWall` payload consumed by {@link paidGuard} and upsell CTAs. */
export type PaidWallContext = {
  family: PaidWallFamily;
  featureId: PaidWallFeatureId;
};

/** Fallback wall context when a guarded route omits `data.paidWall`. */
export const DEFAULT_PAID_WALL_CONTEXT: PaidWallContext = {
  family: 'discovery',
  featureId: 'search',
};

const PAID_WALL_FEATURE_IDS = new Set<PaidWallFeatureId>([
  'search',
  'projects',
  'map-keyword',
  'map-advanced-filters',
  'dashboard-breakdown',
  'reporting',
  'add-to-project',
]);

/** Type guard for the `wall` contact-us query param. */
export function isPaidWallFeatureId(value: string): value is PaidWallFeatureId {
  for (const featureId of PAID_WALL_FEATURE_IDS) {
    if (value === featureId) {
      return true;
    }
  }

  return false;
}

function isPaidWallFamily(value: string): value is PaidWallFamily {
  return (
    value === 'discovery' ||
    value === 'workflow' ||
    value === 'insight' ||
    value === 'reporting'
  );
}

function isPaidWallContextRecord(
  value: unknown
): value is Record<'family' | 'featureId', unknown> {
  return typeof value === 'object' && value !== null;
}

/** Type guard for route `data.paidWall` payloads. */
export function isPaidWallContext(value: unknown): value is PaidWallContext {
  if (!isPaidWallContextRecord(value)) {
    return false;
  }

  const { family } = value;
  const { featureId } = value;

  if (typeof family !== 'string' || typeof featureId !== 'string') {
    return false;
  }

  return isPaidWallFamily(family) && isPaidWallFeatureId(featureId);
}

/** Derives the wall family from a feature id when only the feature is known. */
export function inferPaidWallFamily(
  featureId: PaidWallFeatureId
): PaidWallFamily {
  switch (featureId) {
    case 'search':
    case 'map-keyword':
    case 'map-advanced-filters':
      return 'discovery';
    case 'projects':
    case 'add-to-project':
      return 'workflow';
    case 'dashboard-breakdown':
      return 'insight';
    case 'reporting':
      return 'reporting';
  }
}
